/**
 * GlobalRecordingPill — a faixa de gravação ativa, visível em QUALQUER página do portal (igual à bolinha de chamada
 * do celular).
 *
 * Regra: "a ficha do paciente é o foco". A gravação continua ao navegar pra fora da ficha, mas o mic NUNCA fica
 * invisível. Desde 02/10 (caso das duas gravações juntas por horas) a faixa diz DE QUEM é a consulta e desde quando,
 * e tem o PARAR aqui mesmo: um gravador invisível adota a gravação viva e para pelo fluxo normal (áudio no Storage,
 * plaud_recordings, sessão 'finalized', transcrição). É ele também quem atende o "Finalizar a outra" de outra aba e o
 * pedido de parada que chega pelo banco (outro aparelho, migration 436).
 * Some sozinha quando o RecordingControl da ficha está exibindo a gravação (lá tem o Parar dele) — menos no lembrete:
 * com 90 min de gravação ela aparece em qualquer tela e pergunta se a consulta continua (lembreteDeGravacao.ts).
 */

import React, { useCallback, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Square, Loader2 } from 'lucide-react';
import {
  getLiveRecordingInfo,
  isRecordingUiMounted,
  LiveRecordingPublicInfo,
  useAudioRecorder,
  aoFimDaGravacao,
  registrarPedidoDeParada,
} from '../../hooks/admin/useAudioRecorder';
import { registrarParador, pararGravacaoNestaAba, FimDaParada } from '../../lib/gravacao/umaPorComputador';
import { deveLembrar, adiar, type Adiado } from '../../lib/gravacao/lembreteDeGravacao';

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(1.35); }
`;
const slideIn = keyframes`
  from { opacity: 0; transform: translate(-50%, 12px); }
  to { opacity: 1; transform: translate(-50%, 0); }
`;
const girar = keyframes`to { transform: rotate(360deg); }`;

const Faixa = styled.div<{ $alerta?: boolean }>`
  position: fixed;
  bottom: 18px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 10px;
  width: max-content;
  max-width: calc(100vw - 32px);
  padding: 8px 8px 8px 16px;
  border-radius: 14px;
  background: #1f1a17;
  color: #fff;
  font-family: 'Satoshi', sans-serif;
  font-size: 13px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35), ${p => p.$alerta ? '0 0 0 2px #f59e0b' : '0 0 0 1px rgba(239, 68, 68, 0.35)'};
  animation: ${slideIn} 0.25s ease-out;
  @media (prefers-reduced-motion: reduce) { animation: none; }
  @media (max-width: 560px) {
    left: 16px; right: 16px; bottom: 12px; transform: none; width: auto; max-width: none;
    flex-wrap: wrap; animation: none;
  }
`;
const RedDot = styled.span`
  width: 9px; height: 9px; border-radius: 50%; background: #ef4444; flex-shrink: 0;
  animation: ${pulse} 1.2s ease-in-out infinite;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;
const Texto = styled.span`
  display: flex; align-items: baseline; gap: 6px; min-width: 0; flex: 1 1 auto;
  .quem { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .quem b { font-weight: 600; }
  .tempo { color: #f0b9a4; font-variant-numeric: tabular-nums; white-space: nowrap; }
`;
const Acoes = styled.span`
  display: flex; gap: 6px; flex-shrink: 0;
  @media (max-width: 560px) { width: 100%; button { flex: 1; } }
`;
const Btn = styled.button<{ $parar?: boolean }>`
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  min-height: 34px; padding: 6px 12px; border-radius: 9px; font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer;
  border: 1px solid ${p => p.$parar ? '#ef4444' : '#ffffff30'};
  background: ${p => p.$parar ? '#ef4444' : 'transparent'};
  color: #fff;
  svg { width: 13px; height: 13px; }
  &:disabled { opacity: 0.6; cursor: default; }
  &:focus-visible { outline: 2px solid #f0b9a4; outline-offset: 2px; }
  .girando { animation: ${girar} 0.9s linear infinite; }
`;

/** O gravador invisível: adota a gravação viva desta aba e sabe pará-la pelo fluxo normal. */
function ParadorInvisivel({ patientId, patientName, source, onParando }: {
  patientId: string; patientName: string; source: 'portal' | 'nurse_note';
  onParando: (sim: boolean) => void;
}) {
  const { stopRecording } = useAudioRecorder(patientId, patientName, source, { invisivel: true });
  const parar = useCallback((): Promise<FimDaParada> => new Promise(res => {
    if (!getLiveRecordingInfo()) { res('sem_gravacao'); return; }
    onParando(true);
    const sair = aoFimDaGravacao((_sessao, fim) => { sair(); onParando(false); res(fim); });
    stopRecording().catch(() => { sair(); onParando(false); res('falhou'); });
  }), [stopRecording, onParando]);
  useEffect(() => {
    registrarParador(parar);
    registrarPedidoDeParada(() => { void parar(); });
    return () => { registrarParador(null); registrarPedidoDeParada(null); };
  }, [parar]);
  return null;
}

export default function GlobalRecordingPill() {
  const [info, setInfo] = useState<LiveRecordingPublicInfo | null>(null);
  const [uiMounted, setUiMounted] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  // a gravação que ESTE gravador está parando (ele fica montado até o áudio salvar)
  const [parando, setParando] = useState<LiveRecordingPublicInfo | null>(null);
  // "Continuar gravando" no lembrete dos 90 min: cala a pergunta desta gravação por 30 min
  const [adiado, setAdiado] = useState<Adiado>(null);
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const pt = i18n.language === 'pt';

  // Poll leve (memória local, sem rede): estado da gravação viva + timer
  useEffect(() => {
    const tick = () => {
      setInfo(getLiveRecordingInfo());
      setUiMounted(isRecordingUiMounted());
      setNow(Date.now());
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, []);

  const alvo = info || parando;
  const onParando = useCallback((sim: boolean) => {
    setParando(sim ? getLiveRecordingInfo() : null);
  }, []);

  const chave = alvo ? `${alvo.patientId}:${alvo.startTimestamp}` : '';
  const lembrar = !!alvo && !parando && deveLembrar(alvo.startTimestamp, now, chave, adiado);
  // o lembrete também vai para o nome da aba: com o portal atrás de outra janela, é o que aparece
  useEffect(() => {
    if (!lembrar) return;
    const original = document.title;
    document.title = `● ${pt ? 'Gravação ligada' : 'Recording on'} · ${original}`;
    return () => { document.title = original; };
  }, [lembrar, pt]);

  const parador = alvo ? (
    <ParadorInvisivel key={`${alvo.patientId}:${alvo.startTimestamp}`} patientId={alvo.patientId}
      patientName={alvo.patientName} source={alvo.source} onParando={onParando} />
  ) : null;

  if (!alvo || (uiMounted && !parando && !lembrar)) return parador;

  const secs = Math.max(0, Math.round((now - alvo.startTimestamp) / 1000));
  const hh = Math.floor(secs / 3600);
  const mm = String(Math.floor((secs % 3600) / 60)).padStart(2, '0');
  const ss = String(secs % 60).padStart(2, '0');
  const tempo = hh > 0 ? `${hh}:${mm}:${ss}` : `${mm}:${ss}`;
  const desde = new Date(alvo.startTimestamp).toLocaleTimeString(pt ? 'pt-BR' : 'en-US', { hour: '2-digit', minute: '2-digit' });

  return (
    <>
      {parador}
      <Faixa role={lembrar ? 'alert' : 'status'} aria-live={lembrar ? 'assertive' : 'polite'} data-faixa={lembrar ? 'lembrete' : 'gravando'} $alerta={lembrar}>
        <RedDot aria-hidden />
        <Texto>
          <span className="quem">
            {parando
              ? <>{pt ? 'Salvando a gravação de ' : 'Saving the recording of '}<b>{alvo.patientName}</b>…</>
              : lembrar
                ? <>{pt ? 'Ainda em consulta com ' : 'Still in the visit with '}<b>{alvo.patientName}</b>? {pt ? 'Gravando desde' : 'Recording since'} {desde}</>
                : <>{pt ? 'Gravando a consulta de ' : 'Recording the visit of '}<b>{alvo.patientName}</b> · {pt ? 'desde' : 'since'} {desde}</>}
          </span>
          {!parando && <span className="tempo">{tempo}</span>}
        </Texto>
        <Acoes>
          {lembrar ? (
            <Btn type="button" onClick={() => setAdiado(adiar(chave, Date.now()))}
              title={pt ? 'A consulta continua: pergunto de novo em 30 min' : 'The visit goes on: I will ask again in 30 min'}>
              {pt ? 'Continuar gravando' : 'Keep recording'}
            </Btn>
          ) : (
            <Btn type="button" disabled={!!parando}
              onClick={() => navigate(alvo.returnTo, { state: { focusRecordingTab: Date.now() } })}
              title={pt ? 'Voltar à ficha desta gravação' : 'Back to the chart of this recording'}>
              {pt ? 'Voltar à ficha' : 'Back to chart'}
            </Btn>
          )}
          <Btn type="button" $parar disabled={!!parando} onClick={() => { void pararGravacaoNestaAba(); }}
            title={pt ? 'Parar e salvar esta gravação' : 'Stop and save this recording'}>
            {parando ? <Loader2 className="girando" aria-hidden /> : <Square aria-hidden />}
            {parando ? (pt ? 'Salvando…' : 'Saving…') : (pt ? 'Parar e salvar' : 'Stop and save')}
          </Btn>
        </Acoes>
      </Faixa>
    </>
  );
}
